import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { createPermissionState, type PermissionState } from '@ai-content-hub/hooks';

import { PERMISSION_CODES } from '@/constants/permissionCatalog';
import { SCREEN_ROUTE_RULES } from '@/config/screenPermissions';
import { useAuthStore } from '@/store/authStore';
import { useSelectedTenantStore } from '@/store/selectedTenantStore';
import { isSuperAdmin, canManageTenantPermissionSettings } from '@/utils/tenantRbacPermissions';
import {
    permissionsKeys,
    useEffectivePermissionsQuery,
    useScreenRulesQuery,
} from '@/data/permissions';

/**
 * Builds the effective-permission gate for the current subject. Mount its
 * result into CoreProvider's `permissions` prop; superadmin (staff +
 * `isSuperAdmin`) bypasses every check, matching the pre-EAA-1667 behavior.
 */
export function useWebContentPermissions(): PermissionState {
    const user = useAuthStore((s) => s.user);
    const authMode = useAuthStore((s) => s.authMode);
    const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
    const userId = user?.id !== undefined ? String(user.id) : undefined;
    const queryClient = useQueryClient();

    /*
     * EAA-1842: a member's permission set is resolved BY the acting tenant, so
     * asking for it before one is chosen is not an early read of the right answer
     * — it is a different question, and the backend answers it with
     * `{effective_permissions: [], source: "none"}`.
     *
     * That is what made a fully-permissioned account land on the 403 wall. This
     * hook mounts in WebContentCoreProvider, ABOVE the router, and used to enable
     * the query the instant `isAuthenticated` flipped; `X-Tenant-Id` comes from
     * `selectedTenantStore`, which for a multi-membership identity is only filled
     * in when the person picks from the switcher in MainLayout — a component that
     * has not mounted yet. The request therefore went out unscoped, came back
     * empty, and (keyed by user id alone) never refetched, so the empty set became
     * permanent: no menu items, every route denied.
     *
     * Platform staff are exempt because they carry no acting tenant at all —
     * `main.tsx`'s `getActingTenantId` deliberately sends no header for them, and
     * their scope comes from `?tenant_id=` per request instead. An embed session
     * is exempt for the mirror-image reason: its token already names one tenant,
     * and an iframe has no switcher to offer.
     */
    const actingTenantId = useSelectedTenantStore((s) => s.selectedTenantId);
    const isPlatformStaff = Boolean(user?.is_staff || user?.is_superuser);
    const isEmbedSession = authMode === 'embed_api';
    const actingTenantMissing =
        isAuthenticated && !isPlatformStaff && !isEmbedSession && !actingTenantId;

    const effectiveQuery = useEffectivePermissionsQuery(
        isAuthenticated && !actingTenantMissing,
        userId,
        isPlatformStaff ? null : actingTenantId
    );
    const screenRulesQuery = useScreenRulesQuery(isAuthenticated, userId);

    useEffect(() => {
        if (!isAuthenticated) {
            void queryClient.removeQueries({ queryKey: permissionsKeys.all });
        }
    }, [isAuthenticated, queryClient]);

    // EAA-1762: the last set the server actually confirmed, kept so a failed
    // REFETCH cannot revoke the UI. `effectiveQuery.data` goes undefined
    // whenever the entry is evicted or the key changes (it is keyed by user id,
    // which changes shape between login and reload: portal-login returns a
    // numeric id, /tenants/portal-me returns `sub` as a string), and the old
    // `data?.effectivePermissions ?? []` then read as "this account has no
    // permissions" — an empty sidebar and a "No access" dashboard for an
    // account that holds a full permission group.
    //
    // EAA-1842: it is scoped to (subject, acting tenant), because the set is. The
    // same identity is sys-viewer in one tenant and sys-editor in another, so
    // carrying the previous tenant's codes across a switch would grant write
    // buttons the new tenant denies, for as long as the refetch takes.
    const confirmedScope = `${userId ?? ''}:${actingTenantId ?? ''}`;
    const lastConfirmed = useRef<string[]>(EMPTY_PERMISSIONS);
    const lastConfirmedScope = useRef<string>(confirmedScope);
    if (lastConfirmedScope.current !== confirmedScope) {
        lastConfirmedScope.current = confirmedScope;
        lastConfirmed.current = EMPTY_PERMISSIONS;
    }
    const fetched = effectiveQuery.data?.effectivePermissions;
    if (fetched) {
        lastConfirmed.current = fetched;
    }

    const effectivePermissions = fetched ?? lastConfirmed.current;
    const screenRules = screenRulesQuery.data ?? null;
    const loading = effectiveQuery.isFetching;
    // `isFetched` is also true after a FAILED fetch, which told every gate the
    // permission set was known-empty and skipped the loading branch entirely.
    // Loaded means the server answered — this run, or on a run we still hold.
    const loaded = effectiveQuery.isSuccess || lastConfirmed.current.length > 0;
    const error = effectiveQuery.error
        ? formatUnknownError(effectiveQuery.error, 'Failed to load permissions')
        : null;
    const screenRulesLoaded = screenRulesQuery.isFetched;
    const screenRulesError = screenRulesQuery.error
        ? formatUnknownError(screenRulesQuery.error, 'Failed to load screen rules')
        : null;

    // EAA-1842: refetch through the query client, not through the two query result
    // objects. TanStack returns a NEW result object on every render, so depending
    // on them made this callback — and therefore the whole `PermissionState` memo
    // below — change identity every render, which re-derived MainLayout's antd
    // `items` array every pass (see .cursor/rules/react-performance.mdc).
    const retryPermissions = useCallback(() => {
        void queryClient.refetchQueries({ queryKey: permissionsKeys.all });
    }, [queryClient]);

    return useMemo(() => {
        const hasUser = Boolean(user);
        const superAdmin = authMode === 'staff' && isSuperAdmin(user);
        const canManageTenantPermissions =
            canManageTenantPermissionSettings(user) ||
            superAdmin ||
            effectivePermissions.includes(PERMISSION_CODES.TENANT_TOKENS_MANAGE);

        return createPermissionState({
            effectivePermissions,
            screenRules,
            defaultScreenRules: SCREEN_ROUTE_RULES,
            hasUser,
            superAdmin,
            permissionsLoading: loading,
            permissionsLoaded: loaded,
            permissionsError: error,
            screenRulesLoaded,
            screenRulesError,
            canManageTenantPermissions,
            actingTenantMissing,
            retryPermissions,
        });
    }, [
        user,
        authMode,
        effectivePermissions,
        loading,
        loaded,
        error,
        screenRules,
        screenRulesLoaded,
        screenRulesError,
        actingTenantMissing,
        retryPermissions,
    ]);
}

const EMPTY_PERMISSIONS: string[] = [];

function formatUnknownError(error: unknown, fallback: string): string {
    return error instanceof Error ? error.message : fallback;
}
